import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { LegalEntitiesService as LegalEntitiesServiceApi, COUNTRY_CODE } from '@cemex-core/cmx-api';
import { ILegalEntity } from '@cemex-core/cmx-api/models/legal-entities/legal-entity';
import { USER_TYPE } from '../models/user-type.enum';
import { timeout } from '../helpers/timeout';
import { TranslateService } from '@ngx-translate/core';
import { AuthState } from '../typings';
import { SessionService } from '@cemex-core/angular-services-v8';

/**
 * This service is used to manage the legal entities of the user.
 * @deprecated use SessionService instead and methods currentLegalEntity and setLegalEntity
 */
export const LEGAL_ENTITY_STORAGE_KEY = 'user_legal_entity';

@Injectable()
export class LegalEntitiesService {
  legalEntityLoader = new BehaviorSubject<boolean>(false);
  legalEntityList = new BehaviorSubject<ILegalEntity[]>([]);
  legalEntityMessage = new BehaviorSubject<string>('');
  legalEntityActive = new BehaviorSubject<ILegalEntity>(undefined);
  userType$ = new BehaviorSubject<string>(null);
  private sessionService: SessionService = inject(SessionService);

  private LEGAL_ENTITY_MIN_CHAR_TO_SEARCH = 4;
  private userType: USER_TYPE;
  private legalEntityFullList: ILegalEntity[] = [];
  private activeFilter: string;
  private page = 1;
  private end = false;

  constructor(
    private api: LegalEntitiesServiceApi,
    private translate: TranslateService,
  ) {}

  public async initialize(auth: AuthState) {
    this.userType = auth.profile.userType as USER_TYPE;
    this.userType$.next(this.userType);
    const currentLegalEntity = this.sessionService.currentLegalEntityValue as ILegalEntity;
    await this.getLegalEntities();
    // Check if legal entity exists in the legal entity list of the user
    let legalEntity = !currentLegalEntity
      ? currentLegalEntity
      : this.legalEntityFullList.find(
          (entity) =>
            entity.legalEntityId === currentLegalEntity.legalEntityId ||
            entity.legalEntityTypeCode === '' + currentLegalEntity.legalEntityId,
        );
    if (legalEntity) {
      this.legalEntityActive.next(legalEntity);
    } else if (legalEntity === undefined) {
      this.legalEntityActive.next(currentLegalEntity);
    } else {
      legalEntity = this.getLegalEntityFromUser(auth);
      legalEntity && this.legalEntityActive.next(legalEntity);
    }
    // Handle case if active legal entity is not set in session
    // Must be selected from list based on id from user profile.
    if (!legalEntity && legalEntity !== undefined && auth.profile) {
      this.setActiveLegalEntity(auth.profile.customerId);
    }
  }

  public async getLegalEntities() {
    try {
      this.legalEntityLoader.next(true);
      if (this.userType === USER_TYPE.CUSTOMER) {
        const response = await this.api.getAllLegalEntities().toPromise();
        this.legalEntityFullList = response.legalEntities;
        this.legalEntityList.next(response.legalEntities);
      } else {
        const response = await this.api.getOneLegalEntity().toPromise();
        this.legalEntityFullList = response.legalEntities;
        this.legalEntityMessage.next(this.translate.instant('views.sidebar.legal.warning'));
        this.legalEntityList.next([]);
      }
      this.legalEntityLoader.next(false);
    } catch (error) {
      this.legalEntityLoader.next(false);
    }
  }

  public setActiveLegalEntity(legalEntityId: number) {
    let legalEntity = this.legalEntityFullList.find(
      (entity) => entity.legalEntityId === legalEntityId || entity.legalEntityTypeCode === '' + legalEntityId,
    );
    if (legalEntity) {
      this.saveLegalEntityToStorage(legalEntity);
      this.legalEntityActive.next(legalEntity);
    } else if (this.legalEntityFullList.length > 0) {
      legalEntity = this.legalEntityFullList[0];
      this.saveLegalEntityToStorage(legalEntity);
      this.legalEntityActive.next(legalEntity);
    } else {
      throw new Error(
        'No legal entity available to set as active. ' +
          'This issue is probably caused by insufficient user/customer data.',
      );
    }
  }

  public searchLegalEntity(key: string) {
    this.end = false;
    this.activeFilter = key;
    this.legalEntityMessage.next('');
    if (this.userType === USER_TYPE.CUSTOMER) {
      const filteredList = this.legalEntityFullList.filter(
        (entity) =>
          entity.legalEntityTypeCode.toLowerCase().search(key.toLowerCase()) !== -1 ||
          entity.legalEntityDesc.toLowerCase().search(key.toLowerCase()) !== -1,
      );
      this.legalEntityList.next(filteredList);
      filteredList.length === 0 &&
        this.legalEntityMessage.next(this.translate.instant('views.sidebar.legal.no_matches'));
    } else {
      this.legalEntityList.next([]);
      key.length >= this.LEGAL_ENTITY_MIN_CHAR_TO_SEARCH
        ? this.filterLegalEntities(key)
        : this.legalEntityMessage.next(this.translate.instant('views.sidebar.legal.warning'));
    }
  }

  public async loadNextPage() {
    if (this.end) {
      return;
    }
    this.page += 1;
    try {
      this.legalEntityLoader.next(true);
      const response = await this.api.getFilteredLegalEntities(this.activeFilter, this.page).toPromise();
      if (response.count === 0) {
        this.legalEntityLoader.next(false);
        this.end = true;
        return;
      }
      this.legalEntityFullList = [...this.legalEntityFullList, ...response.legalEntities];
      this.legalEntityList.next(this.legalEntityFullList);
      this.legalEntityLoader.next(false);
      if (response.count < 100) {
        this.end = true;
      }
    } catch (error) {
      this.legalEntityLoader.next(false);
    }
  }

  protected async filterLegalEntities(key: string) {
    await timeout(200);
    if (this.activeFilter !== key) {
      return;
    }
    this.page = 1;
    try {
      this.legalEntityLoader.next(true);
      const response = await this.api.getFilteredLegalEntities(key, this.page).toPromise();
      this.legalEntityFullList = response.legalEntities;
      this.legalEntityList.next(response.legalEntities);
      if (response.totalCount > 1) {
        this.legalEntityMessage.next(
          this.translate.instant('views.sidebar.legal.results', { count: response.totalCount }),
        );
      } else if (response.totalCount === 1) {
        this.legalEntityMessage.next(
          this.translate.instant('views.sidebar.legal.result', { count: response.totalCount }),
        );
      } else {
        this.legalEntityMessage.next(this.translate.instant('views.sidebar.legal.no_matches'));
      }
      if (response.totalCount < 100) {
        this.end = true;
      }
      this.legalEntityLoader.next(false);
    } catch (error) {
      this.legalEntityLoader.next(false);
    }
  }

  protected saveLegalEntityToStorage(legalEntity: ILegalEntity) {
    this.sessionService.setLegalEntity(legalEntity as any);
  }

  private getLegalEntityFromUser(auth: AuthState): ILegalEntity {
    if (!auth.customer) {
      return null;
    }

    return {
      legalEntityCode: auth.customer.customerCode,
      legalEntityId: auth.customer.customerId,
      legalEntityDesc: auth.customer.customerDesc,
      countryCode: auth.country as COUNTRY_CODE,
      address: { addressId: 0, links: { self: '' } },
      legalEntityType: { links: { self: '' } },
      legalEntityTypeCode: auth.customer.customerCode,
      block: { billingBlock: false, orderBlock: false, deliveryBlock: false },
    };
  }
}
